import { useParams } from 'react-router-dom';

import {
  IndicatorFactType,
  PlotReportFactLabelFilter,
  PlotReportFactLabelNameEnum,
} from '@/api/rest/resources/types/fact';
import { useGetRadiusLabel } from '@/components/MapOverview/components/MapRadiusContextProvider';
import { useHiddenFactConfigurationByFactTypes } from '@/hooks/useWidgetConfigurationByOrganizationId';
import { assertIsDefined } from '@/utils/assert';

import { useGlobalPlotReport } from './usePlotReportForGlobalPlot';
import { usePlotReportForPlot } from './usePlotReportForPlot';
import { filterValidLabels, filterValidLabelsForScenario, getFact, getFacts } from './utils';

/**
 * @param plotIdArg Falls back to the plotId in the URL if no value is provided.
 * @param factTypes List of fact types to be fetched.
 * @param labelFilters Fact labels to filter the facts (not sent to the API, filtered in the FE side).
 * @param radiusLabel Radius fact label to filter the facts.
 */
export const usePlotFacts = <T = unknown>(
  factTypes: IndicatorFactType[],
  plotIdArg?: string | null,
  labelFilters?: PlotReportFactLabelFilter,
  radiusLabel?: Record<PlotReportFactLabelNameEnum.landscape_radius_m, string>,
) => {
  const routeParams = useParams();
  const plotId = plotIdArg ?? routeParams.plotId;
  const hiddenFacts = useHiddenFactConfigurationByFactTypes(factTypes);

  assertIsDefined(plotId, 'plotId');

  // Since BE ignore label filters for benchmark and landuse, we filter those out before hitting the API to avoid multiple duplicate calls
  const plotReportFactLabelFilters = labelFilters ? filterValidLabelsForScenario(labelFilters) : {};
  const queryParams = {
    ...plotReportFactLabelFilters,
    fact_names: factTypes.filter((factType) => !hiddenFacts.includes(factType)).join(','),
    ...radiusLabel,
  };

  const plotReport = usePlotReportForPlot(plotId, queryParams).data;

  // But we pass all filters to getFact to fetch the right fact from the API response (eg: choose between high/low/mod benchmark facts)
  const labels = labelFilters ? filterValidLabels(labelFilters) : {};
  return getFacts<T>(plotReport, factTypes, labels);
};

export const useGlobalPlotFact = <T = unknown>(type: IndicatorFactType, labelFilters?: PlotReportFactLabelFilter) => {
  const plotReport = useGlobalPlotReport().data;

  // But we pass all filters to getFact to fetch the right fact from the API response (eg: choose between high/low/mod benchmark facts)
  const labels = labelFilters ? filterValidLabels(labelFilters) : {};
  return getFact<T>(plotReport, type, labels, false);
};

export const usePlotFactsByRadius = <T = unknown>(types: IndicatorFactType[]) => {
  const routeParams = useParams();

  const radiusLabel = useGetRadiusLabel();

  return usePlotFacts<T>(types, routeParams.plotId || null, undefined, radiusLabel);
};
